<template>
    <CqModal
        heading="Nieuwe medewerker toevoegen"
        :showModal="employee !== null"
        :preventClosing="actionsDisabled"
        @closeModal="closeModal"
    >
        <form v-if="employee" ref="form" @submit="createEmployee">
            <CqTextInput label="Initialen" v-model="initials" />
            <CqTextInput label="Voornaam" v-model="firstName" required />
            <CqTextInput label="Voornamen" v-model="firstNames" />
            <CqTextInput label="Tussenvoegsel" v-model="namePrefixes" />
            <CqTextInput label="Achternaam" v-model="lastName" required />
            <CqTextInput label="E-mailadres" v-model="email" type="email" required />
            <CqTextInput label="Telefoonnummer" v-model="phone" type="tel" required />
            <CqButtonBar>
                <CqButton label="Annuleren" :disabled="actionsDisabled" @click="closeModal" />
                <CqButton primary label="Toevoegen" icon="plus" :disabled="actionsDisabled" @click="createEmployee" />
            </CqButtonBar>
        </form>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Model, Prop, Ref} from 'vue-facing-decorator';
    import {CqModal, CqTextInput, CqButtonBar, CqButton} from '@cquential/ui';
    import {ApiClient, CompanyEmployee, ApiError} from '@cquential/api';
    import {CompanyWithDeploymentsAndEmployees} from '@/types/api-object-extensions';
    import Notify from '@/components/modals/Notify.vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqTextInput,
            CqModal,
        },
        emits: ['created:employee', 'closeModal'],
    })
    export default class CompanyEmployeeCreateModal extends Vue {
        @Model() public employee!: CompanyEmployee | null;
        @Prop({required: true}) public company!: CompanyWithDeploymentsAndEmployees;
        @Prop({type: Boolean}) public isPending!: boolean;
        @Ref() public form!: HTMLFormElement | null;

        private saving: boolean = false;

        public get actionsDisabled(): boolean {
            return this.saving || this.isPending;
        }

        public get firstName(): string {
            return this.employee?.firstName ?? '';
        }

        public set firstName(value: string) {
            this.employee = {...this.employee!, firstName: value};
        }

        public get firstNames(): string {
            return this.employee?.firstNames ?? '';
        }

        public set firstNames(value: string) {
            this.employee = {...this.employee!, firstNames: value};
        }

        public get initials(): string {
            return this.employee?.initials ?? '';
        }

        public set initials(value: string) {
            this.employee = {...this.employee!, initials: value};
        }

        public get namePrefixes(): string {
            return this.employee?.namePrefixes ?? '';
        }

        public set namePrefixes(value: string) {
            this.employee = {...this.employee!, namePrefixes: value};
        }

        public get lastName(): string {
            return this.employee?.lastName ?? '';
        }

        public set lastName(value: string) {
            this.employee = {...this.employee!, lastName: value};
        }

        public get email(): string {
            return this.employee?.email ?? '';
        }

        public set email(value: string) {
            this.employee = {...this.employee!, email: value};
        }

        public get phone(): string {
            return this.employee?.phone ?? '+31';
        }

        public set phone(value: string) {
            this.employee = {...this.employee!, phone: value};
        }

        public validateNewEmployee(): boolean {
            if (!this.form) {
                return false;
            }

            if (!this.form.checkValidity()) {
                this.form.reportValidity();

                return false;
            }

            return true;
        }

        public async createEmployee(): Promise<void> {
            if (!this.company?.openShiftMarketplaceSyncEnabled || !this.employee) {
                return;
            }

            if (!this.validateNewEmployee()) {
                return;
            }

            this.saving = true;
            try {
                const employeeId: number = await ApiClient.instance.storeCompanyEmployee(this.employee);

                const createdEmployee: CompanyEmployee = {
                    ...this.employee,
                    id: employeeId,
                };

                this.$emit('created:employee', createdEmployee);

                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Medewerker aangemaakt.', type: 'success'});
            } catch (error: unknown) {
                if (error instanceof ApiError && error.response?.data?.errors && 'email' in error.response.data.errors) {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Het gegeven e-mailadres is al in gebruik of ongeldig.', type: 'error'});
                } else {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Fout bij het aanmaken van de medewerker.', type: 'error'});
                }
            } finally {
                this.saving = false;
            }
        }

        public closeModal(): void {
            this.$emit('closeModal');
        }
    }
</script>
